import { useEffect, useState } from "react";

import { useCreateProject, useEditProject } from "../mutations";
import { validationRules } from "../../../shared/utils/validationRules";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";
import { LocationData } from "../../../shared/types";
import { useProject } from "../../projectOverview/hooks/useProject";
import { useProjects } from "./useProjects";
import { Asset } from "../types";

export const useAddProject = ({ isEdit, projectId }: { isEdit: boolean, projectId?: string }) => {

    const createProject = useCreateProject();
    const editProject = useEditProject();

    const { isPending: isProjectDetailsPending, projectData, removeCache } = useProject(projectId);
    const { removeCachePinned, removeCacheUnpinned } = useProjects({
        searchKey: '',
        portfolioId: undefined,
        isPinned: false,
        quickFilters: {
            region: null,
            programme: null,
            site: null,
        },
        phase_name: null,
        phase_type_id: null,
        page: 1,
        limit: 10,
    });

    const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

    const [formData, setFormData] = useState<{
        title: string | null;
        description: string | null;
        portfolio: AutocompleteOption | null;
        programme: AutocompleteOption | null;
        workstream: string | null;
        project_year: string | null;
        project_scope: string | null;
        wbs_code_id: string | null;
        project_budget: string | null;
        site_name: string | null;
        network: string | null;
        region: string | null;
        county: string | null;
        what3_words: string | null;
        coamArea: string | null;
        location: LocationData | null;
        planned_start_date: string | null;
        planned_end_date: string | null;
        planned_commissioning_date: string | null;
        planned_close_up_date: string | null;
        phase_template: string | null;
        lat: string;
        long: string;
        work_type_id: string | null;
        work_group_id: string | null;
    }>({
        title: null,
        description: null,
        portfolio: null,
        programme: null,
        workstream: null,
        project_year: null,
        project_scope: null,
        wbs_code_id: null,
        project_budget: null,
        site_name: null,
        network: null,
        region: null,
        county: null,
        coamArea: null,
        location: null,
        what3_words: null,
        planned_start_date: null,
        planned_end_date: null,
        planned_commissioning_date: null,
        planned_close_up_date: null,
        phase_template: null,
        lat: '',
        long: '',
        work_type_id: null,
        work_group_id: null,
    });

    const [assetErrors, setAssetErrors] = useState<Record<number, Record<string, string | null>>>({});
    const [assets, setAssets] = useState<Asset[]>([
        {
            id: 1,
            asset_programme: null,
            narms_value: "",
            asset_category: null,
            asset_description: "",
            asset_type: null,
            iln_number_id: "",
            functional_location: "",
            asset_budget: "",
            asset_id: undefined,
        }
    ]);

    const [stakeholders, setStakeholders] = useState<{ label: string, value: string }[]>([]);
    const [openStakeholderDialog, setOpenStakeholderDialog] = useState(false);

    const validationConfig: Record<string, string[]> = {
        title: ["is_empty", "only_string"],
        description: ["is_empty"],
        portfolio: ["is_empty"],
        programme: ["is_empty"],
        workstream: ["is_empty"],
        project_year: ["is_empty"],
        project_scope: ["is_empty"],
        wbs_code_id: ["is_empty"],
        project_budget: ["is_empty", "decimal_number"],
        site_name: ["is_empty"],
        location: ["is_empty"],
        network: ["is_empty"],
        region: ["is_empty"],
        county: ["is_empty"],
        coamArea: ["is_empty"],
        planned_start_date: ["is_empty"],
        planned_end_date: ["is_empty"],
        planned_commissioning_date: ["is_empty"],
        planned_close_up_date: ["is_empty"],
        phase_template: ["is_empty"],
        work_type_id: ["is_empty"],
        work_group_id: ["is_empty"],
    };

    const assetValidationConfig: Record<string, string[]> = {
        asset_programme: ["is_empty"],
        narms_value: ["is_empty"],
        asset_category: ["is_empty"],
        asset_type: ["is_empty"],
        asset_description: ["is_empty"],
        iln_number_id: ["is_empty"],
        functional_location: ["is_empty"],
        asset_budget: ["is_empty", "decimal_number"],
    };

    const validateField = (key: string, value: any): string | null => {
        const rules = validationConfig[key];
        if (!rules) return null;
        for (const ruleName of rules) {
            const rule = validationRules[ruleName];
            if (rule) {
                const error = rule(value);
                if (error) return error;
            }
        }
        return null;
    };

    const validateAllFields = (): { isAllFieldValid: boolean, errors: any } => {
        let newErrors: Record<string, string | null> = {};
        Object.keys(validationConfig).forEach((key) => {
            const error = validateField(key, (formData as any)[key]);
            if (error) newErrors[key] = error;
        });
        setFieldErrors(newErrors);
        const isAllFieldValid = Object.values(newErrors).every((error) => !error);
        return { isAllFieldValid, errors: newErrors };
    };

    const validateAssetField = (key: string, value: any): string | null => {
        const rules = assetValidationConfig[key];
        if (!rules) return null;
        for (const ruleName of rules) {
            const rule = validationRules[ruleName];
            if (rule) {
                const error = rule(value);
                if (error) return error;
            }
        }
        return null;
    };

    const validateAssets = (): boolean => {
        let newAssetErrors: Record<number, Record<string, string | null>> = {};
        assets.forEach((asset) => {
            let assetFieldErrors: Record<string, string | null> = {};
            Object.keys(assetValidationConfig).forEach((key) => {
                if (key === "narms_value" && asset.asset_programme && asset.asset_programme.id !== 6) {
                    return;
                }
                const error = validateAssetField(key, (asset as any)[key]);
                if (error) assetFieldErrors[key] = error;
            });
            if (Object.values(assetFieldErrors).some((error) => error)) {
                newAssetErrors[asset.id] = assetFieldErrors;
            }
        });
        setAssetErrors(newAssetErrors);
        return Object.keys(newAssetErrors).length === 0;
    };

    const handleFieldChange = (key: string, value: any) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        const error = validateField(key, value);
        setFieldErrors((prev) => ({ ...prev, [key]: error }));
    };

    const handleAssetChange = (id: number, key: keyof Asset, value: any) => {
        setAssets((prev) =>
            prev.map((asset) => (asset.id === id ? { ...asset, [key]: value } : asset))
        );

        // Validate the changed field and update errors
        const error = validateAssetField(key as string, value);
        setAssetErrors((prev) => ({
            ...prev,
            [id]: {
                ...(prev[id] || {}),
                [key]: error,
            },
        }));
    };

    const handleAddAsset = () => {
        const newId = assets.length ? assets[assets.length - 1].id + 1 : 1;
        setAssets((prev) => [
            ...prev,
            {
                id: newId,
                asset_programme: null,
                narms_value: "",
                asset_category: null,
                asset_description: "",
                asset_type: null,
                iln_number_id: "",
                functional_location: "",
                asset_budget: "",
            },
        ]);
    };

    const handleRemoveAsset = (id: number) => {
        setAssets((prev) => prev.filter((a) => a.id !== id));
    };

    const handleAddStakeholder = (stakeholder: { label: string; value: string }) => {
        setStakeholders((prev) => [...prev, stakeholder]);
    };

    const handleRemoveStakeholder = (id: string) => {
        setStakeholders((prev) => prev.filter((s) => s.value !== id));
    };

    const hasAnyError = (errors: Record<string, any>) => {
        return Object.values(errors).some(value => value !== null);
    };

    const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {

        setSubmitMessage(null);
        setIsSubmitting(true);

        const { isAllFieldValid, errors: errorInFields } = validateAllFields();
        validateAssets();

        const hasAssetErrors = Object.values(assetErrors || {}).some(asset => hasAnyError(asset));

        if (!isAllFieldValid || (!isEdit && hasAssetErrors)) {
            setIsSubmitting(false);
            return;
        };

        const formatDate = (dateString: string | null): string | null => {
            if (!dateString) return null;
            const date = new Date(dateString);
            return date.toISOString();
        };

        const payload = {
            wbs_code_id: 1,
            project_title: formData?.title ?? "",
            project_description: formData?.description ?? "",
            portfolio_id: formData?.portfolio?.id,
            programme_id: formData?.programme?.id,
            work_stream_id: formData?.workstream,
            project_scope: formData?.project_scope ?? "",
            project_budget: Number(formData?.project_budget),
            site_id: formData?.site_name,
            region_id: formData?.region,
            network_id: formData?.network,
            county_id: formData?.county,
            area_id: formData?.coamArea,
            address: formData?.location?.address ?? "",
            longitude: formData?.location?.coordinates[0],
            latitude: formData?.location?.coordinates[1],
            phase_template_id: formData?.phase_template,
            planned_start_date: formatDate(formData?.planned_start_date),
            planned_end_date: formatDate(formData?.planned_end_date),
            planned_commissioning_date: formatDate(formData?.planned_commissioning_date),
            planned_close_up_date: formatDate(formData?.planned_close_up_date),
            project_year: formData?.project_year,
            template_id: 1,
            work_type_id: formData?.work_type_id,
            work_group_id: formData?.work_group_id,
            asset_details: Array.isArray(assets) ? assets.map(asset => ({
                programme_id: asset.asset_programme?.id,
                asset_category: asset.asset_category,
                asset_type: asset.asset_type,
                narms_value: (asset.asset_programme && asset.asset_programme.id == 6) ? Number(asset.narms_value) : null,
                iln_id: asset.iln_number_id,
                functional_location: asset?.functional_location ?? "",
                asset_budget: Number(asset.asset_budget),
                description: asset?.asset_description ?? "",
                ...isEdit ? { asset_id: asset.asset_id } : {},
            })) : [],
            stake_holders_details: stakeholders.map(s => parseInt(s.value)),
        };

        try {
            if (isEdit && projectId) {
                await editProject.mutateAsync({ projectId, payload });
                removeCache();
                removeCachePinned();
                removeCacheUnpinned();
            } else {
                await createProject.mutateAsync(payload);
            }
            setSubmitMessage({
                type: 'success',
                message: 'Project created successfully!'
            });
            setFormData({
                title: null,
                description: null,
                portfolio: null,
                programme: null,
                workstream: null,
                project_year: null,
                project_scope: null,
                wbs_code_id: null,
                project_budget: null,
                site_name: null,
                network: null,
                region: null,
                county: null,
                coamArea: null,
                location: null,
                what3_words: null,
                planned_start_date: null,
                planned_end_date: null,
                planned_commissioning_date: null,
                planned_close_up_date: null,
                phase_template: null,
                lat: '',
                long: '',
                work_type_id: null,
                work_group_id: null,
            });
            setAssets([{
                id: 1,
                asset_programme: null,
                narms_value: "",
                asset_category: null,
                asset_description: "",
                iln_number_id: "",
                asset_type: null,
                functional_location: "",
                asset_budget: ""
            }]);
            setStakeholders([]);
        } catch (error: any) {
            setSubmitMessage({
                type: 'error',
                message: error?.response?.data?.message || error?.message || 'Failed to create project. Please try again.'
            });
        } finally {
            setIsSubmitting(false);
        }

    };

    useEffect(() => {
        if (isEdit && projectData && !isProjectDetailsPending) {
            setFormData({
                title: projectData.title || null,
                description: projectData.description || null,
                portfolio: projectData.portfolio_id ? { id: projectData.portfolio_id, label: projectData.portfolio_name } : null,
                programme: projectData.programme_id ? { id: projectData.programme_id, label: projectData.programme_reference } : null,
                project_year: projectData.project_year ? String(projectData.project_year) : null,
                workstream: projectData.workstream_id || null,
                project_scope: projectData.project_scope || null,
                wbs_code_id: projectData.wbs_code_id ? String(projectData.wbs_code_id) : null,
                project_budget: projectData.project_budget ? String(projectData.project_budget) : null,
                site_name: projectData.site_id || null,
                network: projectData.org_id || null,
                region: projectData.region_id || null,
                county: projectData.county_id || null,
                coamArea: projectData.area_id || null,
                location: {
                    coordinates: projectData.coordinates ? [(projectData.coordinates.coordinates[0]), (projectData.coordinates.coordinates[1])] : [0, 0],
                    address: projectData.address || '',
                    postcode: projectData.postcode || '',
                    placeName: projectData.address || '',
                    what3_words: projectData.what3_words || '',
                    country: projectData.county_id || '',
                },
                what3_words: projectData.what3_words || null,
                planned_start_date: projectData.planned_start_date || null,
                planned_end_date: projectData.planned_end_date || null,
                planned_commissioning_date: projectData.planned_commissioning_date || null,
                planned_close_up_date: projectData.planned_close_up_date || null,
                phase_template: projectData.project_template_id || null,
                lat: projectData.latitude || '',
                long: projectData.longitude || '',
                work_type_id: projectData.work_type_id || null,
                work_group_id: projectData.work_group_id || null,
            });
        }
    }, [isEdit, projectData, isProjectDetailsPending]);

    return {
        formData, fieldErrors, stakeholders, assets, assetErrors, openStakeholderDialog, isSubmitting, submitMessage,
        setOpenStakeholderDialog, handleAddAsset, handleRemoveAsset, handleSubmit, setFormData, setSubmitMessage,
        handleFieldChange, handleAssetChange, handleAddStakeholder, handleRemoveStakeholder,
    };

};